import React, { useEffect, useState } from 'react';
import './styles/_variables.css';
import './styles/base.css';
import './styles/components.css';
import Header from './components/Header';
import EntriesList from './components/EntriesList';
import EntryForm from './components/EntryForm';
import Footer from './components/Footer';
import { supabase } from './supabaseClient';
import LoginForm from './components/LoginForm';
import ProfileNameForm from './components/ProfileNameForm';
import { normalizeStoredImages } from './lib/imageUtils';

function toStorageType(type) {
  return type === 'image' ? 'note' : type;
}

function toMetadataWithImages(entryLike) {
  const metadata = {
    ...(entryLike.metadata || {}),
    images: entryLike.images ?? [],
  };

  if (entryLike.type === 'image') {
    metadata.contentKind = 'image';
  } else {
    delete metadata.contentKind;
  }

  return metadata;
}

function mapContentToEntry(content) {
  const body = content.body ?? '';
  const metadata = content.metadata && typeof content.metadata === 'object'
    ? content.metadata
    : {};
  const effectiveType =
    content.type === 'image' || metadata.contentKind === 'image'
      ? 'image'
      : content.type;

  return {
    ...content,
    metadata,
    images: normalizeStoredImages(metadata.images),
    storageType: content.type,
    type: effectiveType,
    displayType: effectiveType === 'video' ? 'youtube' : effectiveType,
    content: effectiveType === 'note' || effectiveType === 'image' ? body : '',
    url: effectiveType === 'link' || effectiveType === 'video' ? body : '',
    createdAt: content.created_at,
  };
}

function getUserDisplayName(user) {
  const metadata = user?.user_metadata;
  const storedName =
    metadata?.name ||
    metadata?.full_name ||
    metadata?.display_name ||
    '';

  if (typeof storedName === 'string' && storedName.trim()) {
    return storedName.trim();
  }

  if (typeof user?.email === 'string' && user.email.includes('@')) {
    return user.email.split('@')[0];
  }

  return 'Mystic';
}

function App() {
  const [entries, setEntries] = useState([]);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showProfileNameForm, setShowProfileNameForm] = useState(false);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);

  async function fetchEntries() {
    setIsLoadingEntries(true);

    const { data, error } = await supabase
      .from('content')
      .select('*')
      .order('created_at', { ascending: false });

    setIsLoadingEntries(false);

    if (error) {
      console.error('Fetch error:', error);
      return [];
    }

    return data.map(mapContentToEntry);
  }

  async function updateEntry(updatedEntry) {
    const storageType = toStorageType(updatedEntry.type);
    const payload = {
      type: storageType,
      title: updatedEntry.title,
      body: updatedEntry.type === 'note' || updatedEntry.type === 'image'
        ? updatedEntry.content
        : updatedEntry.url,
      tags: updatedEntry.tags ?? [],
      metadata: toMetadataWithImages(updatedEntry),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('content')
      .update(payload)
      .eq('id', updatedEntry.id)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return mapContentToEntry(data);
  }

  async function createEntry(entry) {
    if (!user) {
      throw new Error('You must be logged in to create an entry.');
    }

    const storageType = toStorageType(entry.type);
    const payload = {
      user_id: user.id,
      type: storageType,
      title: entry.title,
      body: entry.type === 'note' || entry.type === 'image' ? entry.content : entry.url,
      tags: entry.tags ?? [],
      metadata: toMetadataWithImages(entry),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('content')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return mapContentToEntry(data);
  }

  async function deleteEntry(id) {
    const { error } = await supabase
      .from('content')
      .delete()
      .eq('id', id);

    if (error) {
      throw error;
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session) {
        fetchEntries().then(setEntries);
      } else {
        setEntries([]);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session) {
        fetchEntries().then(setEntries);
      } else {
        setEntries([]);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleCreateEntry = async (entry) => {
    const savedEntry = await createEntry(entry);
    setEntries((currentEntries) => [savedEntry, ...currentEntries]);
  };

  const handleDeleteEntry = async (id) => {
    await deleteEntry(id);
    setEntries((currentEntries) => currentEntries.filter((entry) => entry.id !== id));
  };

  const handleEditEntry = async (updatedEntry) => {
    try {
      const savedEntry = await updateEntry(updatedEntry);
      setEntries((currentEntries) =>
        currentEntries.map((entry) => (entry.id === savedEntry.id ? savedEntry : entry))
      );
    } catch (error) {
      console.error('Update error:', error);
      throw error;
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleUpdateProfileName = async (name) => {
    const { data, error } = await supabase.auth.updateUser({
      data: {
        name,
        full_name: name,
        display_name: name,
      },
    });

    if (error) {
      throw error;
    }

    setUser(data.user);
  };

  const filteredEntries = entries
    .filter((entry) => filterType === 'all' || entry.type === filterType)
    .filter((entry) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();

      return (
        entry.title.toLowerCase().includes(query) ||
        (entry.body && entry.body.toLowerCase().includes(query)) ||
        (entry.tags && entry.tags.some((tag) => tag.toLowerCase().includes(query))) ||
        (entry.images && entry.images.some((image) => image.alt.toLowerCase().includes(query)))
      );
    });

  return (
    <div className="app">
      <div className="mystic-sky" aria-hidden="true">
        <span className="mystic-orb orb-a"></span>
        <span className="mystic-orb orb-b"></span>
        <span className="mystic-orb orb-c"></span>
        <span className="mystic-star star-a"></span>
        <span className="mystic-star star-b"></span>
        <span className="mystic-star star-c"></span>
        <span className="mystic-star star-d"></span>
      </div>

      <Header
        onSearch={setSearchQuery}
        searchQuery={searchQuery}
        filterType={filterType}
        onFilterChange={setFilterType}
        user={user}
        userDisplayName={getUserDisplayName(user)}
        onEditName={() => setShowProfileNameForm(true)}
        onLogout={handleLogout}
        onShowLogin={() => setShowLogin(true)}
      />

      <main className="main-content app-shell">
        <div className="container">
          <div className="toolbar-row">
            <button
              className="btn btn-primary toolbar-add-btn"
              onClick={() => setShowEntryForm(true)}
              disabled={!user}
            >
              Add New Item
            </button>
            <div className="entry-count-pill">
              {filteredEntries.length} visible archive{filteredEntries.length === 1 ? '' : 's'}
            </div>
          </div>

          <EntriesList
            entries={filteredEntries}
            loading={isLoadingEntries}
            onDelete={handleDeleteEntry}
            onEdit={handleEditEntry}
          />
        </div>
      </main>

      <Footer />

      {showEntryForm && (
        <EntryForm
          onSubmit={handleCreateEntry}
          onCancel={() => {
            setShowEntryForm(false);
          }}
        />
      )}

      {showLogin && (
        <LoginForm
          onLogin={(email, password) =>
            supabase.auth.signInWithPassword({ email, password })
          }
          onRegister={(name, email, password) =>
            supabase.auth.signUp({
              email,
              password,
              options: {
                data: {
                  name,
                  full_name: name,
                },
              },
            })
          }
          onCancel={() => setShowLogin(false)}
        />
      )}

      {showProfileNameForm && (
        <ProfileNameForm
          initialName={getUserDisplayName(user)}
          onSave={handleUpdateProfileName}
          onCancel={() => setShowProfileNameForm(false)}
        />
      )}
    </div>
  );
}

export default App;
